import React from 'react';
import DsButton from '../../common/DsButton';

type EventDetailsParticipantsPanelProps = {
  view: any;
};

export default function EventDetailsParticipantsPanel({ view }: EventDetailsParticipantsPanelProps) {
  const {
    ArtistBubbleView,
    EventMembersVisibilityConfirmModal,
    assignmentButtonDisabled,
    assignmentButtonLabel,
    assignmentSaving,
    buildParticipantProfile,
    canEditCastingSettings,
    canEditParticipants,
    canViewCastingSettings,
    canViewParticipants,
    closeMembersVisibilityConfirmModal,
    eventId,
    eventReadOnlyTitle,
    getParticipantImageStyle,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityToggle,
    handleRemoveManualParticipant,
    manualParticipantRemovingId,
    membersVisibilityButtonLabel,
    membersVisibilityConfirmState,
    membersVisibilitySaving,
    membersVisibleToArtists,
    navigate,
    openAssignmentSettings,
    openManualParticipantModal,
    participantBubbleArtists,
    participantSections,
    participantSummary,
    participantView,
    setParticipantProfile,
    setParticipantView,
  } = view;

  if (!canViewParticipants && !canViewCastingSettings) return null;

  return (
    <>
      <EventMembersVisibilityConfirmModal
        open={membersVisibilityConfirmState.open}
        saving={membersVisibilitySaving}
        unpublishedEventLabels={membersVisibilityConfirmState.unpublishedEventLabels}
        currentEventLabel={membersVisibilityConfirmState.currentEventLabel}
        onClose={closeMembersVisibilityConfirmModal}
        onConfirmGroupAll={handleMembersVisibilityConfirmGroupAll}
        onConfirmSingle={handleMembersVisibilityConfirmSingle}
      />

      <section
        className="event-panel participants-panel event-detail-primary-participants"
        id="casting-section"
        data-section="casting"
        data-testid="event-participants-panel"
      >
        <div className="panel-heading">
          <div>
            <h3>参加アーティスト</h3>
          </div>
          {canViewParticipants ? (
            <div className="panel-actions participants-panel__actions">
              <DsButton
                variant={membersVisibleToArtists ? 'secondary' : 'primary'}
                size="sm"
                onClick={handleMembersVisibilityToggle}
                disabled={!canEditParticipants || membersVisibilitySaving}
                loading={membersVisibilitySaving}
                aria-pressed={membersVisibleToArtists}
                aria-label="参加アーティストの公開切り替え"
                title={
                  !canEditParticipants
                    ? eventReadOnlyTitle
                    : membersVisibleToArtists
                      ? '非公開に切り替え'
                      : '公開に切り替え'
                }
              >
                {membersVisibilityButtonLabel}
              </DsButton>
            </div>
          ) : null}
        </div>

        {canViewParticipants ? (
          <>
            <div className="participants-toolbar">
              <div className="segmented participants-view-toggle" role="group" aria-label="表示形式">
                <button
                  type="button"
                  className={`segmented__item${participantView === 'list' ? ' is-active' : ''}`}
                  onClick={() => setParticipantView('list')}
                  aria-label="リスト表示"
                  title="リスト表示"
                >
                  <i className="fa-solid fa-list" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  className={`segmented__item${participantView === 'bubble' ? ' is-active' : ''}`}
                  onClick={() => setParticipantView('bubble')}
                  aria-label="バブル表示"
                  title="バブル表示"
                >
                  <i className="fa-solid fa-spinner" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div className="participants-body">
              {participantSections.length === 0 ? (
                <div className="participants-empty">参加アーティストがいません。</div>
              ) : participantView === 'list' ? (
                <div className="participants-list">
                  {participantSections.map((section: any) => (
                    <div className="participants-group" key={section.id}>
                      <div className="participants-group__title">{section.label}</div>
                      <div className="participants-group__list">
                        {section.artists.map((artist: any) => (
                          <div
                            key={artist.artist_id}
                            role="button"
                            tabIndex={0}
                            className="participant-row"
                            onClick={() => setParticipantProfile(buildParticipantProfile(artist))}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return;
                              event.preventDefault();
                              setParticipantProfile(buildParticipantProfile(artist));
                            }}
                          >
                            <div className="participant-identity">
                              <div className="participant-avatar" style={getParticipantImageStyle(artist)}>
                                {!artist.profile_picture && (artist.name || '').charAt(0)}
                              </div>
                              <div className="participant-main">
                                <div className="participant-badges">
                                  {artist.is_concertmaster ? (
                                    <span className="participant-concertmaster-badge">コンサートマスター</span>
                                  ) : null}
                                  {artist.is_chief ? (
                                    <span className="participant-chief-badge">首席</span>
                                  ) : null}
                                  {Array.isArray(artist.custom_roles) && artist.custom_roles.length
                                    ? artist.custom_roles.map((role: any) => (
                                        <span
                                          key={`${role?.id || role?.name || 'role'}-badge`}
                                          className="participant-role-badge"
                                        >
                                          {role?.name || role?.role_name || '役職'}
                                        </span>
                                      ))
                                    : null}
                                  {artist.manual_participant ? (
                                    <span className="participant-manual-badge">契約なし</span>
                                  ) : null}
                                </div>
                                <div className="participant-name">
                                  <span>{artist.name || '—'}</span>
                                </div>
                              </div>
                            </div>
                            <a
                              className="participant-link"
                              href={`/admin/workspaces/artist-management/artists/${artist.artist_id}`}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                              aria-label="アーティスト詳細を開く"
                              title="アーティスト詳細を開く"
                            >
                              詳細
                            </a>
                            {artist.manual_participant && canEditParticipants ? (
                              <button
                                type="button"
                                className="participant-remove-manual"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRemoveManualParticipant(artist);
                                }}
                                disabled={manualParticipantRemovingId === String(artist.artist_id)}
                                title="契約なし参加を解除"
                              >
                                {manualParticipantRemovingId === String(artist.artist_id) ? '解除中…' : '解除'}
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {participantSummary.total > 0 ? (
                    <div className="participants-summary">
                      <div className="participants-summary__row">
                        <span className="participants-summary__label">性別:</span>
                        <span>{participantSummary.genderText}</span>
                      </div>
                      <div className="participants-summary__row">
                        <span className="participants-summary__label">年齢:</span>
                        <span>{participantSummary.ageText}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <ArtistBubbleView
                  artists={participantBubbleArtists}
                  showInlineCard
                  cardVariant="profile"
                  onOpenDetail={(artistId: string) => navigate(`/admin/workspaces/artist-management/artists/${artistId}`)}
                  getSizeBoost={(artist: any) => {
                    if (artist?.is_concertmaster) return 2;
                    if (artist?.is_chief) return 1.5;
                    return 0;
                  }}
                />
              )}
            </div>
          </>
        ) : null}

        <div className="panel-footer">
          {canViewParticipants ? (
            <>
              <DsButton
                variant="secondary"
                onClick={openManualParticipantModal}
                disabled={!canEditParticipants}
                title={!canEditParticipants ? eventReadOnlyTitle : '契約なしで参加アーティストを追加'}
              >
                契約なし参加を追加
              </DsButton>
              <DsButton
                variant="secondary"
                onClick={() => navigate(`/admin/workspaces/event-operations/events/${eventId}/stage-seating`)}
                title="シーティング作成を開く"
              >
                シーティング
              </DsButton>
            </>
          ) : null}
          {canViewCastingSettings ? (
            <>
              <DsButton
                variant="secondary"
                onClick={() => navigate(`/admin/workspaces/event-operations/events/${eventId}/casting-registration`)}
                disabled={!canEditCastingSettings}
                title={!canEditCastingSettings ? eventReadOnlyTitle : undefined}
              >
                キャスティング設定
              </DsButton>
              <DsButton
                variant="secondary"
                onClick={() => navigate(`/admin/workspaces/event-operations/events/${eventId}/casting-progress`)}
                disabled={!canEditCastingSettings}
                title={!canEditCastingSettings ? eventReadOnlyTitle : undefined}
              >
                参加者パート設定
              </DsButton>
              <DsButton
                variant="secondary"
                onClick={() => navigate(`/admin/workspaces/event-operations/events/${eventId}/participant-artists`)}
                disabled={!canEditCastingSettings}
                title={!canEditCastingSettings ? eventReadOnlyTitle : undefined}
              >
                参加アーティスト情報
              </DsButton>
              <DsButton
                variant="secondary"
                onClick={openAssignmentSettings}
                disabled={assignmentButtonDisabled}
                loading={assignmentSaving}
                title={!canEditCastingSettings ? eventReadOnlyTitle : undefined}
              >
                {assignmentButtonLabel}
              </DsButton>
            </>
          ) : null}
        </div>
      </section>
    </>
  );
}
